import { useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for modal behavior
 * Handles ESC key press and click outside to close modal
 */
export const useModal = (isOpen, onClose) => {
  // Track where the mousedown event occurred
  const mouseDownTarget = useRef(null);

  // Handle ESC key press
  const handleEscapeKey = useCallback((event) => {
    if (event.key === 'Escape' && isOpen) {
      onClose();
    }
  }, [isOpen, onClose]);

  // Track mousedown location
  const handleBackdropMouseDown = useCallback((event) => {
    // Only track if clicking the backdrop itself, not child elements
    if (event.target === event.currentTarget) {
      mouseDownTarget.current = event.target;
    } else {
      mouseDownTarget.current = null;
    }
  }, []);

  // Handle mouseup - only close if both down and up are on backdrop
  const handleBackdropMouseUp = useCallback((event) => {
    // Only close if:
    // 1. Mouseup is on the backdrop (event.target === event.currentTarget)
    // 2. Mousedown was also on the backdrop (mouseDownTarget.current === event.currentTarget)
    if (event.target === event.currentTarget && mouseDownTarget.current === event.currentTarget) {
      onClose();
    }
    // Reset tracking
    mouseDownTarget.current = null;
  }, [onClose]);

  // Add/remove event listeners
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }
  }, [isOpen, handleEscapeKey]);

  return {
    handleBackdropMouseDown,
    handleBackdropMouseUp
  };
};

export default useModal;